import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { resolveUploadUrl } from '../../utils/mediaUrl';

const CMSBanners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    button_text: '',
    button_link: '',
    display_order: 0,
    is_active: true,
  });
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const res = await api.getEcommerceBanners();
      if (res.success) setBanners(res.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingBanner(null);
    setForm({
      title: '',
      description: '',
      button_text: '',
      button_link: '',
      display_order: banners.length,
      is_active: true,
    });
    setImageFile(null);
    setShowModal(true);
  };

  const handleOpenEdit = (banner) => {
    setEditingBanner(banner);
    setForm({
      title: banner.title || '',
      description: banner.description || '',
      button_text: banner.button_text || '',
      button_link: banner.button_link || '',
      display_order: banner.display_order ?? 0,
      is_active: Boolean(banner.is_active),
    });
    setImageFile(null);
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setImageFile(file || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!editingBanner && !imageFile) {
      setError('Banner image is required');
      return;
    }
    try {
      if (editingBanner) {
        await api.updateEcommerceBanner(editingBanner.id, form, imageFile);
      } else {
        await api.createEcommerceBanner(form, imageFile);
      }
      setShowModal(false);
      loadBanners();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await api.deleteEcommerceBanner(id);
      loadBanners();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSeedDefault = async () => {
    setError('');
    try {
      const res = await api.seedDefaultEcommerceBanner();
      if (res.message) {
        window.alert(res.message);
      }
      loadBanners();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <>
      {error && (
        <div className="alert alert-danger alert-dismissible">
          <button type="button" className="close" onClick={() => setError('')}>&times;</button>
          {error}
        </div>
      )}

      <div className="row mb-3">
        <div className="col-md-12">
          <button type="button" className="btn btn-primary btn-sm mr-2" onClick={handleOpenCreate}>
            <i className="fas fa-plus"></i> New Banner
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleSeedDefault}>
            <i className="fas fa-magic"></i> Add Default CT70 Banner
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Shop Banners</h3>
          <p className="text-muted mb-0 small">
            Carousel slides on the shop homepage. Use a landscape photo or a transparent PNG product cutout for best results (shown sharp on the right over a blurred background).
          </p>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center p-4">Loading...</div>
          ) : (
            <table className="table table-bordered table-hover mb-0">
              <thead>
                <tr>
                  <th style={{ width: 120 }}>Preview</th>
                  <th>Title</th>
                  <th>Button</th>
                  <th style={{ width: 80 }}>Order</th>
                  <th style={{ width: 90 }}>Status</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {banners.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center">No banners yet</td>
                  </tr>
                ) : (
                  banners.map((banner) => (
                    <tr key={banner.id}>
                      <td className="align-middle text-center">
                        {banner.image_url ? (
                          <img
                            src={resolveUploadUrl(banner.image_url)}
                            alt=""
                            style={{ width: 100, height: 40, objectFit: 'cover', borderRadius: 4 }}
                          />
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="align-middle">
                        <strong>{banner.title}</strong>
                        {banner.description && (
                          <div className="text-muted small text-truncate" style={{ maxWidth: 320 }}>
                            {banner.description}
                          </div>
                        )}
                      </td>
                      <td className="align-middle">
                        {banner.button_text ? (
                          <>
                            <span className="badge badge-secondary">{banner.button_text}</span>
                            {banner.button_link && (
                              <div className="small text-muted text-truncate" style={{ maxWidth: 200 }}>
                                {banner.button_link}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="align-middle text-center">{banner.display_order ?? 0}</td>
                      <td className="align-middle">
                        <span className={`badge ${banner.is_active ? 'bg-success' : 'bg-secondary'}`}>
                          {banner.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="align-middle">
                        <button
                          type="button"
                          className="btn btn-sm btn-info mr-1"
                          onClick={() => handleOpenEdit(banner)}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(banner.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1040 }}
            onClick={() => setShowModal(false)}
          ></div>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={{ zIndex: 1050 }}
          >
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">{editingBanner ? 'Edit Banner' : 'New Banner'}</h4>
                  <button type="button" className="close" onClick={() => setShowModal(false)}>
                    <span>&times;</span>
                  </button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="form-group">
                      <label>Heading *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={form.title}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        className="form-control"
                        name="description"
                        rows="3"
                        value={form.description}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Banner image {!editingBanner && '*'}</label>
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control-file"
                        onChange={handleImageChange}
                        required={!editingBanner}
                      />
                      {editingBanner && editingBanner.image_url && !imageFile && (
                        <div className="mt-2">
                          <img
                            src={resolveUploadUrl(editingBanner.image_url)}
                            alt=""
                            style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'cover', borderRadius: 4 }}
                          />
                        </div>
                      )}
                      {imageFile && <small className="form-text text-muted">New file selected</small>}
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Button text</label>
                          <input
                            type="text"
                            className="form-control"
                            name="button_text"
                            value={form.button_text}
                            onChange={handleChange}
                            placeholder="SHOP NOW"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Button link</label>
                          <input
                            type="text"
                            className="form-control"
                            name="button_link"
                            value={form.button_link}
                            onChange={handleChange}
                            placeholder="/shop/category/handheld-computers"
                          />
                          <small className="form-text text-muted">Relative path or full URL</small>
                        </div>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Display order</label>
                      <input
                        type="number"
                        className="form-control"
                        name="display_order"
                        value={form.display_order}
                        onChange={handleChange}
                        min="0"
                      />
                    </div>
                    <div className="form-group mb-0">
                      <div className="custom-control custom-checkbox">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="banner_active"
                          name="is_active"
                          checked={form.is_active}
                          onChange={handleChange}
                        />
                        <label className="custom-control-label" htmlFor="banner_active">Active (visible on shop)</label>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-default btn-sm" onClick={() => setShowModal(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm">
                      {editingBanner ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CMSBanners;
